/**
 * RTWO Indexer Engine — Index Job Registry Service
 */

import { INDEXER_SERVICE_IDS } from '../domain-models';
import type {
  IndexJobId,
  RTWOIndexJob,
  RTWOIndexJobStatus,
  RTWOIndexJobType,
  RTWOIndexSourceType,
} from '../domain-models';
import type { IIndexJobRegistryService, IndexJobResult } from '../service-interfaces';
import type { IndexerRepositories } from '../repositories/indexer-repositories';
import type { EventBusAdapterRegistry } from '../registry/adapter-registries';
import { IndexerInternalEventTypes } from '../events/indexer-event-bus';
import { BaseIndexerService, type IndexerServiceContext } from './base-indexer-service';

export class IndexJobRegistryService
  extends BaseIndexerService
  implements IIndexJobRegistryService
{
  readonly serviceId = INDEXER_SERVICE_IDS.JOB_REGISTRY;
  readonly serviceName = 'Index Job Registry';
  readonly description = 'Manages the registry of all index jobs via Event Bus adapter ingestion.';
  readonly integrations = ['Core Engine', 'RPC Platform', 'System Registry'];
  readonly requiresRPCSource = true;
  readonly requiresIndexStorage = false;

  constructor(
    context: IndexerServiceContext,
    private readonly repositories: IndexerRepositories,
    private readonly eventBusRegistry: EventBusAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'structure_ready';
  }

  async createJob(
    jobType: RTWOIndexJobType,
    sourceType: RTWOIndexSourceType,
    sourceReference: string
  ): Promise<IndexJobResult> {
    if (!this.eventBusRegistry.isConfigured()) {
      return {
        success: false,
        jobId: null,
        error:
          'RPC Source Not Connected — Cannot create index jobs until real RPC source is connected.',
        isPlaceholder: true,
        source: 'Indexer Engine — RPC Source Not Connected',
      };
    }

    const adapterResult = await this.eventBusRegistry
      .getActiveAdapter()
      .createIndexJob(jobType, sourceType, sourceReference);

    if (adapterResult?.job) {
      this.repositories.jobs.save(adapterResult.job);
      this.context.eventBus.emit(IndexerInternalEventTypes.JOB_CREATED, {
        jobId: adapterResult.job.jobId,
        jobType,
        sourceType,
      });
    }

    return {
      success: false,
      jobId: null,
      error:
        'RPC Source Not Connected — Cannot create index jobs until real RPC source is connected.',
      isPlaceholder: true,
      source: 'Indexer Engine — RPC Source Not Connected',
    };
  }

  getAllJobs(): RTWOIndexJob[] {
    if (!this.eventBusRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.jobs.findAll();
  }

  getJob(jobId: IndexJobId): RTWOIndexJob | null {
    if (!this.eventBusRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.jobs.findById(jobId);
  }

  getJobsByStatus(status: RTWOIndexJobStatus): RTWOIndexJob[] {
    if (!this.eventBusRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.jobs.findByStatus(status);
  }

  getActiveJobCount(): 0 {
    return 0;
  }
}
